import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useAppState } from "../AppState";
import { api } from "../lib/api";
import { MASSA_API_URL } from "../lib/config";
import { AddrLink, ErrorMsg, Loading, PageHeader } from "../components/Bits";
import { Paginator } from "../components/Paginator";

const PAGE_SIZE = 64; // fixed by the Massa Labs API
const fmt = (n: number) => n.toLocaleString("en-US");

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-2 px-3.5 py-1.5 rounded-full bg-panel border border-border text-xs sm:text-sm text-muted">
      <span className="text-fg font-semibold tabular-nums">{value}</span>
      {label}
    </span>
  );
}

/** Stakers ranked by rolls, same data as explorer.massa.net/mainnet/stakers. */
export function Stakers() {
  const { network } = useAppState();
  const [page, setPage] = useState(0);
  const enabled = network === "mainnet" && !!MASSA_API_URL;
  const q = useQuery({
    queryKey: ["massa-stakers", page],
    queryFn: () => api.massaStakers(page),
    enabled,
    placeholderData: (prev) => prev,
  });
  const total = q.data?.total_stakers ?? 0;
  const rows = q.data?.stakers ?? [];

  return (
    <>
      <Helmet>
        <title>{`Stakers — ${network}`}</title>
      </Helmet>

      <PageHeader title="Stakers" subtitle="Addresses staking rolls on mainnet, ranked by rolls.">
        {total > 0 && <Stat label="stakers" value={fmt(total)} />}
        {q.data?.total_rolls != null && <Stat label="rolls staked" value={fmt(q.data.total_rolls)} />}
      </PageHeader>

      <section className="card p-0 sm:p-0 overflow-hidden">
        <div className="flex items-center gap-3 px-4 sm:px-6 py-4 sm:py-5">
          <h2 className="text-base sm:text-lg font-semibold">Top stakers</h2>
        </div>

        {!enabled ? (
          <div className="px-4 sm:px-6 pb-5 text-muted text-sm">Staker data is not available.</div>
        ) : q.isLoading ? (
          <div className="px-4 sm:px-6 pb-5"><Loading /></div>
        ) : q.isError ? (
          <div className="px-4 sm:px-6 pb-5"><ErrorMsg err={q.error} /></div>
        ) : (
          <>
            {/* Mobile: one card per staker. */}
            <ul className="md:hidden flex flex-col gap-2 px-4 pb-4">
              {rows.map((s, i) => (
                <li key={s.hash} className="flex flex-col gap-2.5 p-3.5 rounded-xl bg-bg border border-border">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted tabular-nums">#{page * PAGE_SIZE + i + 1}</span>
                    <AddrLink addr={s.hash} />
                    <span className="ml-auto text-sm font-semibold tabular-nums">
                      {s.percentage_of_share.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted tabular-nums">
                    <span><span className="text-fg/80">{fmt(s.rolls_count)}</span> rolls</span>
                    <span>{s.rolls_count_value} MAS</span>
                    <span className="ml-auto"><span className="text-fg/80">{fmt(s.produced_blocks)}</span> blocks</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="text-muted text-xs bg-thead">
                  <tr>
                    <th className="text-left py-2.5 pl-4 sm:pl-6 pr-3 w-12">#</th>
                    <th className="text-left py-2.5 px-3">Address</th>
                    <th
                      className="text-right py-2.5 px-3"
                      title="Blocks produced recently, as reported by the Massa Labs API."
                    >
                      New produced blocks
                    </th>
                    <th className="text-right py-2.5 px-3">Rolls</th>
                    <th className="text-right py-2.5 px-3">Rolls value</th>
                    <th className="text-right py-2.5 pr-4 sm:pr-6 pl-3">% of total rolls</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {rows.map((s, i) => (
                    <tr key={s.hash} className="border-t border-border">
                      <td className="py-3 pl-4 sm:pl-6 pr-3 text-muted">{page * PAGE_SIZE + i + 1}</td>
                      <td className="py-3 px-3">
                        <AddrLink addr={s.hash} />
                      </td>
                      <td className="py-3 px-3 text-right">{fmt(s.produced_blocks)}</td>
                      <td className="py-3 px-3 text-right">{fmt(s.rolls_count)}</td>
                      <td className="py-3 px-3 text-right text-muted">{s.rolls_count_value} MAS</td>
                      <td className="py-3 pr-4 sm:pr-6 pl-3 text-right">
                        {s.percentage_of_share.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {enabled && (
          <div className="px-4 sm:px-6 pb-4 border-t border-border">
            <Paginator
              page={page}
              pageSize={PAGE_SIZE}
              hasMore={(page + 1) * PAGE_SIZE < total}
              loading={q.isFetching}
              count={rows.length}
              onPrev={() => setPage((p) => Math.max(0, p - 1))}
              onNext={() => setPage((p) => p + 1)}
            />
          </div>
        )}
      </section>
    </>
  );
}
